import { type IReactionDisposer, makeAutoObservable, reaction, runInAction } from 'mobx'
import { isAxiosError } from 'axios'
import { TableStore } from 'shared/ui/Table'
import modalStore from 'shared/ui/Modal/store/modalStore'
import { ModalTypeList } from 'shared/ui/Modal/store/types'
import { toastStore } from 'shared/ui'
import { logger } from 'shared/lib'
import { ZIndex } from 'shared/constants/zIndex'
import { type PageLayoutStore } from 'shared/layout'
import { RequestLoadData } from 'shared/api/lib'
import type {
  IParamsGetTriggers,
  IResponseGetTriggers,
  IResponseTrigger,
  ITriggerCategoryType,
} from 'entities/Trigger/api/type'
import { type ITrigger, Trigger } from 'entities/Trigger/model/Trigger'
import { TriggerApi } from 'entities/Trigger/api/trigger'
import { UsersApi } from 'entities/Users'
import { type IUiSettingsSourceType } from 'entities/Users/api/types'
import type { IEntitiesStore } from 'entities/EntitiesExport'
import { tableLimitStore } from 'entities/TableLimit'
import { FiltersAndSearchStore } from 'widgets/FiltersAndSearch'

const TRIGGER_COLUMNS_SOURCE_TYPE_MAP: Record<ITriggerCategoryType, IUiSettingsSourceType> = {
  all: 'trigger-table-columns_all',
  sms: 'trigger-table-columns_sms',
  ringless: 'trigger-table-columns_rvm',
  rcs: 'trigger-table-columns_rcs',
}

export type ITriggerListStoreProps = {
  type: ITriggerCategoryType
  pageLayoutStore: PageLayoutStore
}

export class TriggerListStore {
  private _type: ITriggerCategoryType
  private _pageLayoutStore: PageLayoutStore
  private _disposeLoadTriggers: IReactionDisposer | null = null
  private _disposeRequestParams: IReactionDisposer | null = null
  private _deleteTriggerBulkModalId = 'deleteTriggerBulkModal'
  private _filtersAndSearchStore: FiltersAndSearchStore
  private _tableStore: TableStore<ITrigger>

  page = 1
  limit: number | null = tableLimitStore.getLimit('triggers-table') ?? 10
  total = 0
  isFirstLoading = true

  triggersMap: Map<number, ITrigger> = new Map()

  constructor(
    { type, pageLayoutStore }: ITriggerListStoreProps,
    private _entities: IEntitiesStore
  ) {
    this._type = type
    this._pageLayoutStore = pageLayoutStore

    this._tableStore = new TableStore<ITrigger>({
      element: 'trigger',
      sortBy: 'created_at',
      sortOrder: 'desc',
      saveColumns: this._saveColumns,
      getColumns: this._getColumns(),
    })

    this._filtersAndSearchStore = new FiltersAndSearchStore(
      {
        getFilters: () => TriggerApi.getFilters().then(({ data }) => data),
        excludeFilters:
          type === 'rcs' ? new Map([['default', ['workflows.number_id']]]) : undefined,
      },
      this._entities
    )

    makeAutoObservable(this)

    this.reactionLoadTriggers()
  }

  private _saveColumns = async (items: string[]) => {
    try {
      await UsersApi.updateUsersUiSettings({
        source_type: TRIGGER_COLUMNS_SOURCE_TYPE_MAP[this._type],
        items,
      })
    } catch (e) {
      logger.error(e)
    }
  }

  private _getColumns = async (): Promise<string[] | undefined> => {
    try {
      const { data } = await UsersApi.getUsersUiSettings(
        TRIGGER_COLUMNS_SOURCE_TYPE_MAP[this._type]
      )
      const items = data.data?.items
      if (!items?.length) return undefined
      return items
    } catch (e) {
      logger.error(e)
      return undefined
    }
  }

  initTriggersLoad = async () => {
    await this._entities.integrationsStore.fetchIntegrations()
    void this.loadTriggers()
  }

  get tableStore() {
    return this._tableStore
  }

  get filtersAndSearchStore() {
    return this._filtersAndSearchStore
  }

  get triggers() {
    return Array.from(this.triggersMap.values())
  }

  get isNoSearchResults() {
    return !this.isLoading && !this.triggersMap.size && this._filtersAndSearchStore.hasSearchParams
  }

  get isTriggersEmpty() {
    return !this.isLoading && !this.triggersMap.size && !this._filtersAndSearchStore.hasSearchParams
  }

  get requestParams(): IParamsGetTriggers {
    return {
      type: this._type,
      length: this.limit || undefined,
      sortBy: this.tableStore.sortBy,
      sortOrder: this.tableStore.sortOrder,
      search: this._filtersAndSearchStore.params.search,
      filtersList: this._filtersAndSearchStore.params.filtersList,
    }
  }

  get requestParamsPaging(): IParamsGetTriggers {
    return {
      ...this.requestParams,
      page: this.page,
    }
  }

  get pageLayoutStore() {
    return this._pageLayoutStore
  }

  get isLoading() {
    return this._requestLoadTriggers.loading
  }

  private _requestLoadTriggers = new RequestLoadData<boolean>({
    requestCallback: async ({ cancelRequest }, noLoading) => {
      runInAction(() => {
        if (noLoading) {
          this._requestLoadTriggers.setLoading(false)
        }
      })

      try {
        const { data } = await TriggerApi.getTriggers(this.requestParamsPaging, {
          cancelToken: cancelRequest.token,
        })

        this.setGetTriggersResponse(data)
      } catch (error) {
        if (isAxiosError(error) && error.response?.status === 408) {
          logger.warn(
            'TABLE: triggers-table Request timed out, likely due to large data volume. Consider adding more filters to reduce the dataset size.'
          )
          const newLimit = tableLimitStore.reduceLimit('triggers-table', this.limit ?? 10)
          this.limit = newLimit
          tableLimitStore.setLimit('triggers-table', newLimit)
        } else {
          throw error
        }
      }
    },
    finallyCallback: () => {
      runInAction(() => {
        this.isFirstLoading = false
      })
    },
  })

  loadTriggers = async (noLoading?: boolean) => {
    return this._requestLoadTriggers.loadData(noLoading)
  }

  setGetTriggersResponse = ({ data, meta }: IResponseGetTriggers) => {
    this.triggersMap.clear()
    this._tableStore.setRows([])

    this.setTriggerMap(data)
    this._tableStore.setRows(this.triggers)

    this.page = meta.current_page
    this.limit = meta.per_page
    this.total = meta.total
  }

  setTriggerMap = (triggersResponse: IResponseTrigger[]) => {
    triggersResponse.forEach((triggerResponse) => {
      try {
        this.triggersMap.set(triggerResponse.id, new Trigger(triggerResponse, this._entities))
      } catch (e) {
        logger.error(e)
      }
    })
  }

  onPaginationModelChange = (page: number, limit: number) => {
    this.page = page
    this.limit = limit
    tableLimitStore.setLimit('triggers-table', limit)
  }

  reactionLoadTriggers = () => {
    this._disposeLoadTriggers?.()
    this._disposeRequestParams?.()

    this._disposeLoadTriggers = reaction(
      () => this.requestParamsPaging,
      () => this._requestLoadTriggers.debounceLoadData()
    )

    this._disposeRequestParams = reaction(
      () => this.requestParams,
      () => {
        this._requestLoadTriggers.setLoading(true)
        this.page = 1
      }
    )
  }

  clearReactions = () => {
    this._disposeLoadTriggers?.()
    this._disposeRequestParams?.()
  }

  onDelete = (id: number) => {
    let noLoading = true
    this.triggersMap.delete(id)
    if (this.triggersMap.size === 0 && this.page > 1) {
      this.page -= 1
      noLoading = false
    }
    void this.loadTriggers(noLoading)
  }

  bulkDelete = () => {
    const bulkAll = this._tableStore.bulkAllMode
    const ids = this._tableStore.selectedIds as number[]
    const idsLength = ids.length

    const handleDelete = async () => {
      this._requestLoadTriggers.setLoading(true)
      modalStore.removeModal(this._deleteTriggerBulkModalId)

      try {
        await TriggerApi.deleteBulkTriggers(ids, bulkAll)
        toastStore.add({
          title: bulkAll ? 'All triggers deleted' : `Trigger${idsLength > 1 ? 's' : ''} deleted`,
          type: 'success',
        })

        this._tableStore.resetSelected()
        this.triggersMap.clear()
        this.loadTriggers()
      } catch (error) {
        runInAction(() => {
          this._requestLoadTriggers.setLoading(false)
          toastStore.add({
            title: 'Something went wrong. Please try again.',
            type: 'error',
          })
        })
      }
    }

    const handleCancel = () => {
      modalStore.removeModal(this._deleteTriggerBulkModalId)
    }

    modalStore.addModal({
      id: this._deleteTriggerBulkModalId,
      showHeader: true,
      showCloseButton: false,
      showCloseIcon: true,
      width: 280,
      type: ModalTypeList.ALERT,
      zIndex: ZIndex.OVERLAY_MIDDLE,
      title: bulkAll
        ? 'Delete all triggers?'
        : `Delete ${idsLength} trigger${idsLength > 1 ? 's' : ''}?`,
      desc: "The triggers' unique Webhook URL will be lost forever. If you choose to re-create the triggers in the future you will receive new unique Webhook URLs to use.",
      primaryAction: {
        text: 'Delete',
        onAction: handleDelete,
      },
      secondaryAction: {
        text: 'Cancel',
        onAction: handleCancel,
      },
      onClose: handleCancel,
    })
  }

  get type() {
    return this._type
  }
}
